import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CoachLayout } from '../components/CoachLayout';
import { athletesAPI, lessonsAPI } from '../lib/api';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { toast } from 'sonner';
import { ArrowLeft, Download, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadPDF } from '../lib/pdfDownload';
import { useAuth } from '../context/AuthContext';

const ITEMS_PER_PAGE = 10;

export default function AthleteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    try {
      const response = await athletesAPI.getDetails(id);
      setData(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dettagli');
      navigate('/athletes');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/athletes?edit=${id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo atleta?')) return;
    try {
      await athletesAPI.delete(id);
      toast.success('Atleta eliminato');
      navigate('/athletes');
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const markAsPaid = async (lessonId, paymentType) => {
    try {
      await lessonsAPI.markPayment({ lesson_id: lessonId, athlete_id: id, payment_type: paymentType });
      toast.success(`Pagamento ${paymentType === 'coach' ? 'allenatore' : 'struttura'} marcato come pagato!`);
      fetchDetails();
    } catch (error) {
      toast.error('Errore nel marcare il pagamento');
    }
  };

  const unmarkAsPaid = async (lessonId, paymentType) => {
    try {
      await lessonsAPI.unmarkPayment({ lesson_id: lessonId, athlete_id: id, payment_type: paymentType });
      toast.success(`Pagamento ${paymentType === 'coach' ? 'allenatore' : 'struttura'} annullato!`);
      fetchDetails();
    } catch (error) {
      toast.error('Errore nell\'annullare il pagamento');
    }
  };

  const exportPDF = async () => {
    if (!data) return;
    
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text(`Trainers' Manager - Report Atleta: ${data.athlete.name}`, 14, 20);
    
    doc.setFontSize(12);
    doc.text(`Allenatore: ${user?.name || 'N/A'}`, 14, 28);
    doc.text(`Totale Lezioni: ${data.summary.total_lessons}`, 14, 42);
    doc.text(`Compenso Allenatore: € ${data.summary.total_coach_amount.toFixed(2)}`, 14, 49);
    doc.text(`Costo Struttura: € ${data.summary.total_venue_amount.toFixed(2)}`, 14, 56);
    doc.text(`Totale: € ${data.summary.total_amount.toFixed(2)}`, 14, 63);
    doc.text(`Pagato: € ${data.summary.paid_amount.toFixed(2)}`, 14, 70);
    doc.text(`Da Pagare: € ${data.summary.unpaid_amount.toFixed(2)}`, 14, 77);
    
    const tableData = data.lessons.map(lesson => [
      lesson.date,
      lesson.start_time,
      `${lesson.duration_minutes} min`,
      lesson.venue_name,
      lesson.num_athletes,
      `€ ${lesson.coach_amount.toFixed(2)}`,
      `€ ${lesson.venue_amount.toFixed(2)}`,
      `€ ${lesson.total_amount.toFixed(2)}`,
      lesson.coach_payment_status === 'paid' && lesson.venue_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE'
    ]);
    
    autoTable(doc, {
      head: [['Data', 'Ora', 'Durata', 'Struttura', 'Atleti', 'Compenso', 'Struttura', 'Totale', 'Stato']],
      body: tableData,
      startY: 87,
      theme: 'grid',
      headStyles: { fillColor: [204, 255, 0], textColor: [0, 0, 0] },
      styles: { fontSize: 8 }
    });
    
    await downloadPDF(doc, `report-${data.athlete.name.replace(/\s+/g, '-')}.pdf`, {
      title: 'Report atleta',
      text: 'Report atleta pronto',
    });
    toast.success('Report pronto!');
  };

  if (loading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
        </div>
      </CoachLayout>
    );
  }

  if (!data) return null;

  // Ordina le lezioni dalla più recente alla più vecchia
  const sortedLessons = [...data.lessons].sort((a, b) => {
    const dateA = new Date(a.date.split('/').reverse().join('-') + 'T' + a.start_time);
    const dateB = new Date(b.date.split('/').reverse().join('-') + 'T' + b.start_time);
    return dateB - dateA;
  });
  
  const totalPages = Math.ceil(sortedLessons.length / ITEMS_PER_PAGE);
  const paginatedLessons = sortedLessons.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <CoachLayout>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-1 pb-4 sm:pt-2 sm:pb-8 md:pt-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
            <Button
              onClick={() => navigate('/athletes')}
              variant="ghost"
              className="text-zinc-400 hover:text-white p-2 -ml-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div 
              className="cursor-pointer transition-transform hover:scale-110"
              onClick={() => navigate(`/athletes/${id}`)}
            >
              <ProfileAvatar 
                src={data.athlete.profile_photo} 
                name={data.athlete.name}
                size="lg"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-4xl font-black text-white truncate">{data.athlete.name}</h1>
              <p className="text-zinc-400 text-sm truncate">{data.athlete.email || 'Nessuna email'}</p>
            </div>
          </div>
          <Button
            onClick={exportPDF}
            className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl text-sm w-full sm:w-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            Esporta PDF
          </Button>
        </div>

        {/* Dettaglio per allenatore e struttura */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="glass-card p-4 sm:p-5 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-xs sm:text-sm mb-2 sm:mb-3">Compenso Allenatore ({data.coach_name})</div>
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex justify-between">
                <span className="text-zinc-400 text-sm">Totale:</span>
                <span className="text-white font-bold text-base sm:text-lg">€{data.summary.total_coach_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400 text-sm">Pagato:</span>
                <span className="text-green-500 font-bold text-sm sm:text-base">€{data.summary.paid_coach_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400 text-sm">Da pagare:</span>
                <span className="text-red-500 font-bold text-sm sm:text-base">€{data.summary.unpaid_coach_amount.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="glass-card p-4 sm:p-5 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-xs sm:text-sm mb-2 sm:mb-3">Costo Struttura</div>
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex justify-between">
                <span className="text-zinc-400 text-sm">Totale:</span>
                <span className="text-white font-bold text-base sm:text-lg">€{data.summary.total_venue_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400 text-sm">Pagato:</span>
                <span className="text-green-500 font-bold text-sm sm:text-base">€{data.summary.paid_venue_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400 text-sm">Da pagare:</span>
                <span className="text-red-500 font-bold text-sm sm:text-base">€{data.summary.unpaid_venue_amount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Lessons List */}
        <div className="glass-card rounded-xl border border-white/10 overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-white/10">
            <h2 className="text-lg sm:text-xl font-bold text-white">Lezioni ({data.lessons.length})</h2>
          </div>
          
          {data.lessons.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-zinc-400 text-sm">
              Nessuna lezione trovata per questo atleta
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="bg-zinc-900">
                    <tr>
                      <th className="px-3 py-3 text-left text-zinc-400 text-xs whitespace-nowrap">Data</th>
                      <th className="px-3 py-3 text-left text-zinc-400 text-xs whitespace-nowrap">Ora</th>
                      <th className="px-3 py-3 text-left text-zinc-400 text-xs whitespace-nowrap">Struttura</th>
                      <th className="px-3 py-3 text-center text-zinc-400 text-xs whitespace-nowrap">Atleti</th>
                      <th className="px-3 py-3 text-right text-zinc-400 text-xs whitespace-nowrap">Compenso</th>
                      <th className="px-3 py-3 text-right text-zinc-400 text-xs whitespace-nowrap">Struttura</th>
                      <th className="px-3 py-3 text-right text-zinc-400 text-xs whitespace-nowrap">Totale</th>
                      <th className="px-3 py-3 text-center text-zinc-400 text-xs whitespace-nowrap">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLessons.map((lesson) => (
                      <tr key={lesson.lesson_id} className="border-b border-white/5 hover:bg-zinc-900/50">
                        <td className="px-3 py-3 text-white text-sm whitespace-nowrap">{lesson.date}</td>
                        <td className="px-3 py-3 text-white text-sm whitespace-nowrap">{lesson.start_time}</td>
                        <td className="px-3 py-3 text-zinc-300 text-sm whitespace-nowrap">{lesson.venue_name}</td>
                        <td className="px-3 py-3 text-center text-white text-sm whitespace-nowrap">{lesson.num_athletes}</td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end space-x-2">
                            <span className={`font-mono text-sm ${
                              lesson.coach_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                            }`}>€{lesson.coach_amount.toFixed(2)}</span>
                            {lesson.coach_payment_status === 'paid' ? (
                              <Button size="sm" onClick={() => unmarkAsPaid(lesson.lesson_id, 'coach')} className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg">
                                <CheckCircle className="w-3 h-3" />
                              </Button>
                            ) : (
                              <Button size="sm" onClick={() => markAsPaid(lesson.lesson_id, 'coach')} className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg">
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end space-x-2">
                            <span className={`font-mono text-sm ${
                              lesson.venue_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                            }`}>€{lesson.venue_amount.toFixed(2)}</span>
                            {lesson.venue_payment_status === 'paid' ? (
                              <Button size="sm" onClick={() => unmarkAsPaid(lesson.lesson_id, 'venue')} className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg">
                                <CheckCircle className="w-3 h-3" />
                              </Button>
                            ) : (
                              <Button size="sm" onClick={() => markAsPaid(lesson.lesson_id, 'venue')} className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg">
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right text-[#ccff00] font-mono font-bold text-sm whitespace-nowrap">€{lesson.total_amount.toFixed(2)}</td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/lessons/${lesson.lesson_id}`)}
                            className="text-zinc-400 hover:text-[#ccff00] text-xs h-7 px-2"
                          >
                            Dettagli
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center space-x-2 sm:space-x-4 p-3 sm:p-4 border-t border-white/10">
                  <Button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <ChevronLeft className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Indietro</span>
                  </Button>
                  <span className="text-zinc-400 text-xs sm:text-sm">{currentPage} / {totalPages}</span>
                  <Button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <span className="hidden sm:inline">Avanti</span>
                    <ChevronRight className="w-4 h-4 sm:ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="mt-6 sm:mt-8 flex flex-row gap-3 sm:gap-4 justify-between">
          <Button
            onClick={handleEdit}
            className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
          >
            Modifica
          </Button>
          <Button
            onClick={handleDelete}
            variant="destructive"
            className="flex-1 rounded-xl"
          >
            Elimina
          </Button>
        </div>
      </div>
    </CoachLayout>
  );
}
